import {
  Button,
  Checkbox,
  DatePicker,
  FAIcon,
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  LeaderGroupCombobox,
  Textarea,
  TimePicker,
  UserCombobox,
} from '@/components'
import { DepartmentCombobox } from '@/components/combobox/department-combox'
import { FacilityCombobox } from '@/components/combobox/facility-combobox'
import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import VehicleSelect from '@/components/vehicle-select'
import {
  ALL_DEPARTMENTS_QUERY,
  Module,
  ModuleOptionsValue,
} from '@/modules/companies'
import type { VehicleSelectNode } from '@/modules/vehicles'
import { useFormWatch } from '@/utils'
import {
  useAdmin,
  useLeaderGroups,
  useModuleConfig,
  useNodes,
  usePermissions,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { combineDateAndTime } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addMilliseconds, isSameDay, set } from 'date-fns'
import { isMobile } from 'react-device-detect'
import { useEffect, useMemo, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useDepartmentFacilityFromVehicleOnly } from '../hooks'
import { useTimeEntryPreviewData } from '../time-entry-preview.hooks'
import {
  anchorPause,
  getNextPause,
  placeTimeAfter,
  sortSpans,
  validatePauses,
} from '../time-entry-pause'
import type { TimeSpan } from '../time-entry-pause'
import type { TimeEntryFormValues } from '../types'
import type { TimesheetsSalaryWorkTime } from '../types.graphql'
import { TimeEntryPreview } from './time-entry-preview'

interface Props {
  form: UseFormReturn<TimeEntryFormValues>
  salaryWorkTime: TimesheetsSalaryWorkTime
  canSetSalaryCodeManually: boolean
  overtimeRequired: boolean
  /** Lets the registration be split into several entries around pauses. */
  allowPause: boolean
}

export const TimeEntryForm: React.FC<Props> = ({
  form,
  salaryWorkTime,
  canSetSalaryCodeManually,
  overtimeRequired,
  allowPause,
}) => {
  const translations = useTranslate({
    user: 'common.user',
    leaderGroups: 'companies.leader-groups',
    chooseGroup: 'common.choose-group',
    facility: 'common.facility',
    department: 'common.department',
    vehicle: 'common.vehicle',
    from: 'common.from',
    until: 'common.until',
    comment: 'common.comment',
    needsComment: 'common.needs-comment',
    diet: 'common.diet',
    additionalPay: 'common.additional-pay',
    salaryCode: 'common.salary-code',
    overtimeRequired: 'timesheets.error.overtime-mode-required',
    pauses: 'timesheets.time-entry-form.pauses',
    pausesDescription: 'timesheets.time-entry-form.pauses-description',
    addPause: 'timesheets.time-entry-form.add-pause',
    removePause: 'timesheets.time-entry-form.remove-pause',
    pauseFrom: 'timesheets.time-entry-form.pause-from',
    pauseUntil: 'timesheets.time-entry-form.pause-until',
  })

  const { hasPermissions } = usePermissions()
  const canSeeOthers = hasPermissions(
    PERMISSIONS.timesheets.view.otherstimeentry,
    true
  )
  const isAdmin = useAdmin()
  const leaderGroups = useLeaderGroups(Module.TIMESHEETS)
  const [selectedLeaderGroup, setSelectedLeaderGroup] = useState('')
  const [leaderGroupTouched, setLeaderGroupTouched] = useState(false)

  const { moduleActive: salaryModuleActive } = useModuleConfig(Module.SALARY)
  const { getModuleOptionStatus } = useModuleConfig(Module.TIMESHEETS)
  const projectActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_PROJECTS
  )
  const departmentMandatory = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_DEPARTMENT_SELECT_REQUIRED
  )
  const salaryCodeActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_SALARY_CODE_IN_REGISTRATION
  )

  const { data: departmentsData } = useQuery(ALL_DEPARTMENTS_QUERY)
  const companyDepartments = useNodes(departmentsData?.allDepartments)
  const allowedDepartmentIds = useMemo(() => {
    const departments = [
      ...(salaryWorkTime.availableDepartments ?? []),
      ...(salaryWorkTime.defaultDepartment
        ? [salaryWorkTime.defaultDepartment]
        : []),
    ]

    return departments.length > 0
      ? new Set(departments.map(department => department.id))
      : null
  }, [salaryWorkTime.availableDepartments, salaryWorkTime.defaultDepartment])
  const departmentActive =
    companyDepartments.length > 0 ||
    (salaryWorkTime.availableDepartments?.length ?? 0) > 0 ||
    !!salaryWorkTime.defaultDepartment ||
    departmentMandatory
  const departmentFacilityLocked = useDepartmentFacilityFromVehicleOnly()
  const showVehicleSelect =
    departmentActive || projectActive || departmentFacilityLocked

  const userId = useFormWatch(form, 'userId')
  const start = useFormWatch(form, 'start')
  const end = useFormWatch(form, 'end')
  const pauses = useFormWatch(form, 'pauses')

  const effectiveSelectedLeaderGroup =
    !leaderGroupTouched &&
    canSeeOthers &&
    !isAdmin &&
    !selectedLeaderGroup &&
    leaderGroups.myLeaderGroups.length === 1 &&
    leaderGroups.myLeaderGroups[0]
      ? leaderGroups.myLeaderGroups[0].id
      : selectedLeaderGroup

  const selectedLeaderGroupUsers = useMemo(() => {
    if (!effectiveSelectedLeaderGroup) return null
    const group = leaderGroups.myLeaderGroups.find(
      lg => lg.id === effectiveSelectedLeaderGroup
    )
    return new Set(group?.users.map(user => user.id) ?? [])
  }, [effectiveSelectedLeaderGroup, leaderGroups.myLeaderGroups])

  useEffect(() => {
    if (!selectedLeaderGroupUsers || selectedLeaderGroupUsers.has(userId))
      return

    const firstUserId = selectedLeaderGroupUsers.values().next().value
    form.setValue('userId', firstUserId ?? '', { shouldValidate: true })
  }, [form, selectedLeaderGroupUsers, userId])

  /**
   * Start, end and pause validate against each other, so a change to any of
   * them re-runs the whole schema rather than just the edited field.
   */
  function update(values: Partial<TimeEntryFormValues>) {
    for (const [key, value] of Object.entries(values)) {
      form.setValue(key as keyof TimeEntryFormValues, value, {
        shouldDirty: true,
      })
    }
    void form.trigger([
      ...(Object.keys(values) as (keyof TimeEntryFormValues)[]),
      'end',
      'pauses',
    ])
  }

  function handleStartChange(next: Date) {
    const values = form.getValues()
    const dateChanged = !isSameDay(next, values.start)

    update({
      start: next,
      end: dateChanged
        ? set(values.end, {
            year: next.getFullYear(),
            month: next.getMonth(),
            date: next.getDate(),
          })
        : values.end,
      pauses: values.pauses.map(pause => anchorPause(pause, next)),
      overtimeSalaryCodeId: '',
    })
  }

  function handleEndChange(next: Date) {
    update({ end: next, overtimeSalaryCodeId: '' })
  }

  function updatePause(index: number, pause: TimeSpan) {
    update({
      pauses: form
        .getValues('pauses')
        .map((current, idx) => (idx === index ? pause : current)),
    })
  }

  function handlePauseStartChange(index: number, next: Date) {
    const values = form.getValues()
    const pause = values.pauses[index]
    if (!pause) return

    const duration = pause.end.getTime() - pause.start.getTime()
    const start = placeTimeAfter(next, values.start)
    updatePause(index, {
      start,
      end: addMilliseconds(start, Math.max(duration, 0)),
    })
  }

  function handlePauseEndChange(index: number, next: Date) {
    const pause = form.getValues('pauses')[index]
    if (!pause) return

    updatePause(index, {
      start: pause.start,
      end: placeTimeAfter(next, pause.start),
    })
  }

  function handleAddPause() {
    const values = form.getValues()
    const pause = getNextPause(
      { start: values.start, end: values.end },
      values.pauses,
      salaryWorkTime.pauseDuration
    )
    if (pause) update({ pauses: sortSpans([...values.pauses, pause]) })
  }

  function handleRemovePause(index: number) {
    update({
      pauses: form.getValues('pauses').filter((_, idx) => idx !== index),
    })
  }

  function handleVehicleChange(vehicle: VehicleSelectNode | null) {
    const vehicleId = vehicle?.id ?? ''

    const allowedVehicleDepartmentId =
      vehicle?.department?.id &&
      (allowedDepartmentIds === null ||
        allowedDepartmentIds.has(vehicle.department.id))
        ? vehicle.department.id
        : ''

    if (departmentFacilityLocked) {
      // Locked fields mirror the selected vehicle, including clearing them
      update({
        vehicleId,
        ...(departmentActive ? { department: allowedVehicleDepartmentId } : {}),
        ...(projectActive ? { facility: vehicle?.facility?.id ?? '' } : {}),
      })
      return
    }

    update({
      vehicleId,
      ...(departmentActive && allowedVehicleDepartmentId
        ? { department: allowedVehicleDepartmentId }
        : {}),
      ...(projectActive && vehicle?.facility?.id
        ? { facility: vehicle.facility.id }
        : {}),
    })
  }

  const previewData = useTimeEntryPreviewData(
    userId,
    { start, end },
    allowPause
  )

  const pauseErrors = validatePauses({ start, end }, pauses)
  const previewPauseIndexes = pauses.flatMap((_, idx) =>
    pauseErrors[idx] ? [] : [idx]
  )
  const previewPauses = previewPauseIndexes.flatMap(idx => {
    const pause = pauses[idx]
    return pause ? [pause] : []
  })
  const canAddPause =
    start < end &&
    !!getNextPause({ start, end }, pauses, salaryWorkTime.pauseDuration)
  const pauseFieldErrors = form.formState.errors.pauses

  return (
    <div className="@container">
      <div className="grid gap-7 @3xl:grid-cols-2 @3xl:gap-x-10">
        <FieldGroup>
          {canSeeOthers && (
            <>
              {leaderGroups.hasAccess && (
                <Field>
                  <FieldLabel>{translations.leaderGroups}</FieldLabel>
                  <LeaderGroupCombobox
                    mode="single"
                    value={effectiveSelectedLeaderGroup}
                    module={Module.TIMESHEETS}
                    clearable
                    placeholder={translations.chooseGroup}
                    onChange={value => {
                      setLeaderGroupTouched(true)
                      setSelectedLeaderGroup(value)
                    }}
                  />
                </Field>
              )}

              <Controller
                name="userId"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={!!fieldState.error}>
                    <FieldLabel>{translations.user}</FieldLabel>
                    <UserCombobox
                      {...field}
                      mode="single"
                      hideItem={item =>
                        !!selectedLeaderGroupUsers &&
                        !selectedLeaderGroupUsers.has(item.id)
                      }
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
            </>
          )}

          <Controller
            name="start"
            control={form.control}
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.from}</FieldLabel>
                <div className="flex items-end gap-2">
                  <TimePicker
                    value={field.value}
                    onChange={handleStartChange}
                  />
                  <DatePicker
                    className="w-full"
                    mode="single"
                    value={field.value}
                    onChange={date =>
                      date &&
                      handleStartChange(combineDateAndTime(date, field.value))
                    }
                  />
                </div>
              </Field>
            )}
          />

          <Controller
            name="end"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>{translations.until}</FieldLabel>
                <div className="flex items-end gap-2">
                  <TimePicker value={field.value} onChange={handleEndChange} />
                  <DatePicker
                    className="w-full"
                    mode="single"
                    value={field.value}
                    disabledDates={{ before: start }}
                    onChange={date =>
                      date &&
                      handleEndChange(combineDateAndTime(date, field.value))
                    }
                  />
                </div>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          {allowPause && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <FieldContent>
                  <FieldLabel>{translations.pauses}</FieldLabel>
                  <FieldDescription>
                    {translations.pausesDescription}
                  </FieldDescription>
                </FieldContent>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canAddPause}
                  onClick={handleAddPause}
                >
                  <FAIcon icon="plus" />
                  <span>{translations.addPause}</span>
                </Button>
              </div>

              {pauses.map((pause, idx) => {
                const error = pauseFieldErrors?.[idx]?.end?.message

                return (
                  <Field key={idx} data-invalid={!!error}>
                    <div className="flex flex-wrap items-end gap-4">
                      <div className="space-y-2">
                        <FieldLabel>{translations.pauseFrom}</FieldLabel>
                        <TimePicker
                          value={pause.start}
                          onChange={next => handlePauseStartChange(idx, next)}
                        />
                      </div>
                      <div className="space-y-2">
                        <FieldLabel>{translations.pauseUntil}</FieldLabel>
                        <TimePicker
                          value={pause.end}
                          onChange={next => handlePauseEndChange(idx, next)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-md"
                        className="mb-2.5"
                        aria-label={translations.removePause}
                        title={translations.removePause}
                        onClick={() => handleRemovePause(idx)}
                      >
                        <FAIcon icon="trash-alt" />
                      </Button>
                    </div>
                    {error && <FieldError>{error}</FieldError>}
                  </Field>
                )
              })}

              <TimeEntryPreview
                span={{ start, end }}
                pauses={previewPauses}
                paidPauseThreshold={previewData.paidPauseThreshold}
                activities={previewData.activities}
                activitiesLoaded={previewData.activitiesLoaded}
                onPauseChange={(previewIdx, pause) => {
                  const formIdx = previewPauseIndexes[previewIdx]
                  if (formIdx !== undefined) updatePause(formIdx, pause)
                }}
              />
            </div>
          )}
        </FieldGroup>

        <FieldGroup>
          {salaryCodeActive && canSetSalaryCodeManually && (
            <Controller
              name="salaryCodeId"
              control={form.control}
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.salaryCode}</FieldLabel>
                  <SalaryCodeCombobox
                    {...field}
                    mode="single"
                    clearable
                    showInTimeEntries
                    placeholder={translations.salaryCode}
                  />
                </Field>
              )}
            />
          )}

          {overtimeRequired && (
            <Controller
              name="overtimeSalaryCodeId"
              control={form.control}
              render={({ field }) => (
                <Field data-invalid={!field.value}>
                  <FieldLabel>{translations.additionalPay}</FieldLabel>
                  <SalaryCodeCombobox
                    {...field}
                    mode="single"
                    clearable
                    showInManualTimeEntryOvertime
                    placeholder={translations.additionalPay}
                  />
                  {!field.value && (
                    <FieldError>{translations.overtimeRequired}</FieldError>
                  )}
                </Field>
              )}
            />
          )}

          <Controller
            name="comment"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>{translations.comment}</FieldLabel>
                <Textarea
                  {...field}
                  rows={isMobile ? 6 : 4}
                  placeholder={translations.needsComment}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          {!salaryModuleActive && (
            <Controller
              name="diet"
              control={form.control}
              render={({ field }) => (
                <Field orientation="horizontal">
                  <Checkbox
                    {...field}
                    id="time-entry-diet"
                    size="md"
                    onChange={checked => field.onChange(checked === true)}
                  />
                  <FieldLabel htmlFor="time-entry-diet">
                    {translations.diet}
                  </FieldLabel>
                </Field>
              )}
            />
          )}

          {showVehicleSelect && (
            <Controller
              name="vehicleId"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={!!fieldState.error}>
                  <FieldLabel>{translations.vehicle}</FieldLabel>
                  <VehicleSelect
                    mode="single"
                    value={field.value}
                    clearable
                    onChange={handleVehicleChange}
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />
          )}

          <Controller
            name="department"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>{translations.department}</FieldLabel>
                <DepartmentCombobox
                  {...field}
                  mode="single"
                  clearable
                  disabled={departmentFacilityLocked}
                  dataset={salaryWorkTime.availableDepartments}
                  alwaysInclude={salaryWorkTime.defaultDepartment}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          {projectActive && (
            <Controller
              name="facility"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={!!fieldState.error}>
                  <FieldLabel>{translations.facility}</FieldLabel>
                  <FacilityCombobox
                    {...field}
                    mode="single"
                    clearable
                    disabled={departmentFacilityLocked}
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />
          )}
        </FieldGroup>
      </div>
    </div>
  )
}
